import { FC, PropsWithChildren, useLayoutEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import AppLogo from '@/components/ui/AppLogo/AppLogo.tsx';
import Navbar from '@/layouts/Default/Navbar.tsx';
import { Button, Drawer } from 'react-daisyui';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { DefaultLayoutProvider, useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { cn } from '@/utils/classNames.ts';

type LayoutProps = {
  mainClassName?: string;
};

const Layout: FC<LayoutProps> = (props) => {
  return (
    <DefaultLayoutProvider>
      <LayoutInner {...props} />
    </DefaultLayoutProvider>
  );
};

const LayoutInner: FC<LayoutProps> = ({ mainClassName }) => {
  const { containerElementRef, isMenuOpen, setIsMenuOpen } = useDefaultLayoutContext();

  return (
    <>
      <div className="flex min-h-[100dvh]">
        <aside
          className="flex flex-col gap-10 px-7 py-20 w-[250px] min-w-[250px] sticky top-0 h-screen overflow-hidden border-r border-[rgba(204,204,204,0.5)] hidden lg:flex"
          style={{ background: 'linear-gradient(180deg, #0F1B2A 0%, #1A5292 126.68%)' }}
        >
          <div className="flex flex-col items-center gap-2 px-4">
            <Link to={Routes.DASHBOARD}>
              <AppLogo.Default className="w-[194px]" />
            </Link>
          </div>

          <Navbar className="flex" />
        </aside>

        <div className="flex lg:hidden items-center gap-4 px-4 py-3 fixed top-0 left-0 right-0 z-10 bg-[#0F1B2A]">
          <Link to={Routes.DASHBOARD}>
            <AppLogo.Default className="w-[120px]" />
          </Link>
          <Button
            shape="circle"
            color="ghost"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="p-2 ml-auto text-white"
          >
            <Bars3Icon className="w-6 h-6" />
          </Button>
        </div>

        <main
          ref={containerElementRef}
          id="default-layout-content-container"
          className={cn(
            'flex flex-col flex-1 bg-white p-7 gap-8 min-h-screen overflow-auto mt-[52px] lg:mt-0',
            mainClassName
          )}
        >
          <Outlet />
        </main>
      </div>

      <Drawer
        className="z-10"
        end={false}
        side={
          <div
            className="flex flex-col w-[250px] h-[100dvh] overflow-auto py-10 px-7 gap-10"
            style={{ background: 'linear-gradient(180deg, #0F1B2A 0%, #1A5292 126.68%)' }}
          >
            <div className="flex justify-end">
              <Button color="ghost" size="sm" className="text-white" onClick={() => setIsMenuOpen(false)}>
                <XMarkIcon className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex flex-col items-center gap-2 px-4">
              <AppLogo.Default className="w-[160px]" />
            </div>
            <Navbar onClick={() => setIsMenuOpen(false)} className="flex" />
          </div>
        }
        open={isMenuOpen}
        onClickOverlay={() => (isMenuOpen ? setIsMenuOpen(false) : null)}
      />

      <LayoutDrawer />
    </>
  );
};

const InnerDrawer: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={'layout-drawer'}>
      {children}
    </Portal>
  );
};

const LayoutDrawer: FC = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useDefaultLayoutContext();
  const { t } = useTranslation();

  return (
    <Drawer
      className="z-10"
      end={true}
      side={
        <div className="flex flex-col max-w-[100vw] max-h-[100dvh] h-[100dvh] bg-base-100 overflow-auto">
          <div className="bg-base-100 pt-6 pb-4 px-6 flex justify-end sticky top-0 z-10">
            <Button
              color="ghost"
              size="sm"
              endIcon={<XMarkIcon className="w-6 h-6" />}
              onClick={() => setIsDrawerOpen(false)}
            >
              {t('button.close')}
            </Button>
          </div>
          <div id="layout-drawer" className="empty:hidden pb-6 px-6" />
        </div>
      }
      open={isDrawerOpen}
      onClickOverlay={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
    />
  );
};

export default {
  Layout,
  Drawer: InnerDrawer,
};
